import { useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, message, Progress } from "antd";
import { PaperClipOutlined, InboxOutlined } from "@ant-design/icons";
import { subirArchivo } from "../../../../context/actions/solicitudesActions";

export const FileUpload = ({ solicitudId, compact = false }) => {
  const dispatch = useDispatch();
  const { subiendoArchivo } = useSelector(state => state.solicitudesReducer);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileUpload = (file) => {
    // Validar que tenemos solicitudId
    if (!solicitudId) {
      message.error("Error: No se puede subir archivo sin ID de solicitud");
      return false;
    }

    // Validaciones
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/jpg', 
      'image/png',
      'image/gif',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain'
    ];

    if (file.size > maxSize) {
      message.error("El archivo no puede ser mayor a 10MB");
      return false;
    }

    if (!allowedTypes.includes(file.type)) {
      message.error("Tipo de archivo no permitido. Solo se permiten: PDF, imágenes, Word, Excel y texto plano");
      return false;
    }

    dispatch(subirArchivo(solicitudId, file));
    return false; // Prevenir upload automático de antd
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  };

  if (compact) {
    return (
      <div className="compact-upload">
        <input
          ref={fileInputRef}
          type="file"
          onChange={handleFileSelect}
          style={{ display: "none" }}
          accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.xls,.xlsx,.txt"
        />
        <div
          className={`attach-btn ${subiendoArchivo ? 'loading' : ''} ${!solicitudId ? 'disabled' : ''}`}
          onClick={() => !subiendoArchivo && solicitudId && fileInputRef.current?.click()}
          title={!solicitudId ? "No disponible" : subiendoArchivo ? "Subiendo..." : "Adjuntar archivo"}
        >
          <PaperClipOutlined />
        </div>
        
        <style jsx>{`
          .compact-upload {
            display: flex;
            align-items: center;
          }
          
          .attach-btn {
            width: 36px;
            height: 36px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 6px;
            cursor: pointer;
            transition: all 0.2s ease;
            color: #666;
            font-size: 16px;
            border: 1px solid #d9d9d9;
            background: #fff;
          }
          
          .attach-btn:hover {
            color: #1890ff;
            border-color: #1890ff;
            background: #f0f9ff;
          }
          
          .attach-btn.loading {
            color: #1890ff;
            animation: pulse 1.5s infinite;
          }
          
          .attach-btn.disabled {
            color: #ccc;
            cursor: not-allowed;
            border-color: #f0f0f0;
          }
          
          .attach-btn.disabled:hover {
            color: #ccc;
            border-color: #f0f0f0;
            background: #fff;
          }
          
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="file-upload-container">
      <input
        ref={fileInputRef}
        type="file"
        onChange={handleFileSelect}
        style={{ display: "none" }}
        accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.xls,.xlsx,.txt"
        disabled={subiendoArchivo || !solicitudId}
      />
      
      <div 
        className={`custom-dragger ${dragActive ? 'drag-active' : ''} ${subiendoArchivo || !solicitudId ? 'disabled' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => !subiendoArchivo && solicitudId && fileInputRef.current?.click()}
      >
        <div className="upload-content">
          <div className="upload-icon">
            <InboxOutlined />
          </div>
          <div className="upload-text">
            {!solicitudId ? "Selecciona una solicitud primero" : subiendoArchivo ? "Subiendo archivo..." : "Haz clic o arrastra archivos aquí"}
          </div>
          <div className="upload-hint">
            Por imágenes (Word, Excel, PDF, JPG, máx. 10MB)
          </div>
        </div>
      </div>
      
      {subiendoArchivo && (
        <Progress 
          percent={100} 
          status="active" 
          showInfo={false}
          style={{ marginTop: 10 }}
        />
      )}

      <style jsx>{`
        .file-upload-container {
          margin: 4px 0 8px 0;
        }
        
        .custom-dragger {
          border: 2px dashed #d9d9d9;
          border-radius: 8px;
          background: #fafafa;
          transition: all 0.3s ease;
          padding: 24px;
          min-height: 120px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .custom-dragger:hover {
          border-color: #40a9ff;
          background: #f0f9ff;
        }
        
        .custom-dragger.drag-active {
          border-color: #1890ff;
          background-color: #f0f9ff;
        }
        
        .custom-dragger.disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }
        
        .custom-dragger.disabled:hover {
          border-color: #d9d9d9;
          background: #fafafa;
        }
        
        .upload-content {
          text-align: center;
          width: 100%;
        }
        
        .upload-icon {
          font-size: 32px;
          margin-bottom: 8px;
          color: #d9d9d9;
        }
        
        .upload-text {
          color: #333;
          font-size: 14px;
          font-weight: 500;
          margin-bottom: 4px;
        }
        
        .upload-hint {
          color: #999;
          font-size: 12px;
        }
        
        .disabled .upload-text,
        .disabled .upload-hint {
          color: #bbb;
        }
        
        .disabled .upload-icon {
          color: #eee;
        }
      `}</style>
    </div>
  );
};